import { useEffect, useState } from "react"
import {
  Block,
  Button,
  ButtonIcon,
  CheckCircle,
  ChevronLeft,
  HeadingXSmall,
  Link,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  selectDepositById,
  selectWalletAddress,
  useDappSelector,
} from "#/redux-state"
import FailedActionModalBody from "#/shared/components/modals/FailedActionModalBody"
import { links } from "#/shared/constants"
import {
  useModalCustomOptions,
  useSnackbarNotification,
  useUpdateDepositAutoMigration,
} from "#/shared/hooks"
import { usePosthogDepositOptEvent } from "#/shared/hooks/posthog-events"
import { bigIntToFloat, getRecentDepositLock } from "#/shared/utils"

import { BaseModal, BaseModalProps } from "../Modal"

import AssetsBridgingSummary from "./AssetsBridgingSummary"
import AutoMigrateListItem from "./AutoMigrateListItem"

export default function MigrateToMainnetModal({
  onClose,
  ...props
}: BaseModalProps) {
  const [css, theme] = useStyletron()
  const { showSnackbarNotification } = useSnackbarNotification()

  const { emitDepositOptEvent } = usePosthogDepositOptEvent()

  const { depositUuid } = useModalCustomOptions<{
    depositUuid: string
  }>()

  const deposit = useDappSelector((state) =>
    selectDepositById(state, depositUuid),
  )

  const address = useDappSelector(selectWalletAddress)

  const isAutoMigrationEnabled = deposit?.isAutoMigrationEnabled ?? true

  const [isChecked, setIsChecked] = useState(isAutoMigrationEnabled)

  useEffect(() => {
    setIsChecked(isAutoMigrationEnabled)
  }, [isAutoMigrationEnabled, depositUuid])

  const { updateDepositAutoMigration, isPending, isError, reset } =
    useUpdateDepositAutoMigration()

  const handlePosthogEvent = () => {
    if (!deposit) return

    const { lockEnd } = getRecentDepositLock(deposit)
    const walletAddress = address ?? ""

    const depositAmount = bigIntToFloat(BigInt(deposit.amount))
    const depositId = deposit.depositId.toString()

    emitDepositOptEvent(
      depositId,
      walletAddress,
      depositAmount,
      deposit.token,
      lockEnd,
      isChecked,
    )
  }

  const onConfirm = async () => {
    await updateDepositAutoMigration(
      {
        depositUuid,
        isAutoMigrationEnabled: isChecked,
      },
      {
        onSuccess: () => {
          onClose?.({})
          handlePosthogEvent()
          showSnackbarNotification(
            `Auto-bridge is turned ${isChecked ? "on" : "off"}`,
            theme.colors.contentPositive,
            () => <CheckCircle />,
          )
        },
      },
    )
  }

  // We do not want submit form and send request when the value has not changed.
  const hasUserChangedOption = isAutoMigrationEnabled !== isChecked

  return (
    <BaseModal
      autoFocus={false}
      onClose={onClose}
      closeable={!isError}
      overrides={{
        Dialog: {
          style: () => ({
            height: "fit-content",
            "min-height": "667px",
            justifyContent: "start",
          }),
        },
      }}
      {...props}
    >
      <ModalHeader>
        {isError && (
          <ButtonIcon
            shape="circle"
            size="small"
            onClick={reset}
            overrides={{
              BaseButton: {
                style: ({ $theme }) => ({
                  position: "absolute",
                  top: $theme.sizing.scale550,
                  left: $theme.sizing.scale550,
                }),
              },
            }}
          >
            <ChevronLeft size={16} />
          </ButtonIcon>
        )}
        <HeadingXSmall
          className={css({
            textAlign: "center",
          })}
        >
          Bridge to Mainnet
        </HeadingXSmall>
      </ModalHeader>
      {isError ? (
        <FailedActionModalBody
          title="Something went wrong"
          description="Please try again."
          onClickNextStep={reset}
          style={{ marginTop: "auto", marginBottom: "auto" }}
        />
      ) : (
        <>
          <ModalBody>
            <AssetsBridgingSummary />
            <AutoMigrateListItem
              isChecked={isChecked}
              setIsChecked={setIsChecked}
              listItemLabel="Enable Auto-bridging to Mainnet"
              withBorder
            />
            <Block
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: theme.sizing.scale600,
                margin: `${theme.sizing.scale600} 0`,
              })}
            >
              <ParagraphSmall color={theme.colors.contentTertiary}>
                Your assets will arrive ready to use across all Mezo services,
                with full access to your earned mats rewards.
              </ParagraphSmall>
              <Link
                href={links.TERMS_OF_USE}
                target="_blank"
                className={css({
                  textAlign: "center",
                })}
              >
                Terms and conditions
              </Link>
            </Block>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={onConfirm}
              width="100%"
              size="large"
              isLoading={isPending}
              disabled={!hasUserChangedOption}
            >
              Confirm
            </Button>
          </ModalFooter>
        </>
      )}
    </BaseModal>
  )
}
